import z from 'zod';

export const WeightUnit = z
  .enum(['lbs', 'kg'])
  .describe(
    'Unit of measurement for weight, either pounds (lbs) or kilograms (kg).',
  );

export const HeightUnit = z
  .enum(['in', 'cm'])
  .describe(
    'Unit of measurement for height, either inches (in) or centimeters (cm).',
  );

export const ContactMedicalDetailsSchema = z
  .object({
    weight: z
      .number()
      .optional()
      .describe(
        'The individual’s weight, expressed as a numerical value, if available.',
      ),
    weightUnit: WeightUnit.optional(),
    height: z
      .number()
      .optional()
      .describe(
        'The individual’s height, expressed as a numerical value, if available.',
      ),
    heightUnit: HeightUnit.optional(),
    allergies: z
      .string()
      .optional()
      .describe('Known allergies that the individual may have, if any.'),
    medications: z
      .string()
      .optional()
      .describe(
        'List of medications the individual is currently taking, if applicable.',
      ),
    diseases: z
      .string()
      .optional()
      .describe(
        'Any diseases or chronic conditions the individual has been diagnosed with.',
      ),
    smoke: z
      .boolean()
      .describe('Indicates whether the individual is a smoker (true/false).')
      .optional(),
    alcohol: z
      .boolean()
      .describe(
        'Indicates whether the individual consumes alcohol (true/false).',
      )
      .optional(),
    surgicalHistory: z
      .string()
      .optional()
      .describe('A record of the individual’s past surgeries, if any.'),
    familyHistory: z
      .string()
      .optional()
      .describe(
        'Relevant medical history of the individual’s family, such as hereditary conditions.',
      ),
    substances: z
      .string()
      .optional()
      .describe(
        'List of substances, if any, that the individual uses (e.g., recreational drugs).',
      ),
    hasChildren: z
      .boolean()
      .describe('Indicates whether the individual has children (true/false).')
      .optional(),
    numberOfChildren: z
      .number()
      .optional()
      .describe('The number of children the individual has, if applicable.'),
    dateOfLastPregnancy: z
      .string()
      .nullable()
      .describe(
        'The date of the individual’s last pregnancy, in the YYYY-MM-DD format.',
      )
      .optional(),
  })
  .describe(
    'Medical information of the contact, detailing their health and personal medical history. If certain information is unavailable, leave it empty.',
  );

export type ContactMedicalDetails = z.infer<typeof ContactMedicalDetailsSchema>;
